import { useMemo } from 'react'

import { clsx } from 'clsx'

import type { AppearanceStyle } from '@tribeplatform/design-system/types'
import {
  type Glyph,
  GlyphMediaVariant,
  Media,
} from '@tribeplatform/gql-client/types'
import { isImage } from '@tribeplatform/gql-client/utils'
import { useImageUploader } from '@tribeplatform/react-sdk/hooks'
import { FileDropZone } from '@tribeplatform/react-ui-kit/FileUpload'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { noop } from '@tribeplatform/react-ui-kit/hooks'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { EntityImage } from '../common/components/EntityImage.js'
import { Image } from '../common/components/Image.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import { PopoverImagePicker } from '../Picker/PopoverImagePicker.js'
import type {
  ImagePickerTab,
  PickerEmoji,
  PickerIcon,
} from '../Picker/types.js'
import { emojiToImageId, iconToImageId } from '../Picker/utils/picker.utils.js'
import { handleUploadFileError } from '../utils/error.js'

interface Props {
  value: Media
  iconStyle?: AppearanceStyle['icons']
  displayColor?: string
  helperText?: string
  actionTitle?: string
  hasEmoji?: boolean
  hasIcon?: boolean
  hasUpload?: boolean
  onChange: (media: Media) => void
}
export const MediaPicker = ({
  value,
  iconStyle,
  displayColor,
  helperText,
  actionTitle,
  hasEmoji = true,
  hasIcon = true,
  hasUpload = true,
  onChange,
}: Props) => {
  const intl = useI18n()
  const { $t } = intl
  const { uploadImage, isLoading: isUploadingImages } = useImageUploader()

  const pickerTabs = useMemo<ImagePickerTab[]>(
    () =>
      [
        ...(hasEmoji ? ['emoji'] : []),
        ...(hasIcon ? ['icon'] : []),
        ...(hasUpload ? ['upload'] : []),
      ] as ImagePickerTab[],
    [hasEmoji, hasIcon, hasUpload],
  )

  const handleImageChange = async (files: File[]) => {
    if (!files?.length) {
      return
    }

    const file = files?.at(0)

    try {
      const uploadedImage = await uploadImage(file)
      onChange(uploadedImage)
    } catch (error) {
      handleUploadFileError(intl)(error)
    }
  }

  const handleEmojiChange = async (pickerEmoji: PickerEmoji) => {
    const glyph: Glyph = {
      id: emojiToImageId(pickerEmoji),
      text: pickerEmoji.unified,
      variant: GlyphMediaVariant.emoji,
    }
    onChange(glyph)
  }

  const handleIconChange = async (pickerIcon: PickerIcon) => {
    const glyph: Glyph = {
      id: iconToImageId(pickerIcon),
      text: pickerIcon.name,
      variant: GlyphMediaVariant.icon,
    }
    onChange(glyph)
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <FormControl.Label>
          <T id="Generics.Icon" defaultMessage="Icon" />
        </FormControl.Label>
        {!!value && (
          <div className="text-sm font-normal">
            <Link variant="accent" onClick={() => onChange(null)}>
              <T defaultMessage="Remove" id="Generics.Remove" />
            </Link>
          </div>
        )}
      </div>
      <PopoverImagePicker
        tabs={pickerTabs}
        removable={!!value}
        onRemove={() => {
          onChange(null)
        }}
        iconStyle={iconStyle}
        onImageChange={handleImageChange}
        onEmojiChange={handleEmojiChange}
        onIconChange={handleIconChange}
        placement="right"
      >
        <FileDropZone
          type="image"
          accept="image/*"
          allowMultiple={false}
          onDrop={hasUpload ? handleImageChange : noop}
          loading={isUploadingImages}
          className="p-3"
          overlayText=""
          onClick={noop}
        >
          <div className="h-32">
            {value ? (
              <>
                {isImage(value) ? (
                  <Image
                    className={clsx(
                      'block mx-auto object-contain h-32 bg-checkerboard',
                    )}
                    src={value.urls?.small ?? value.url}
                    alt={$t({
                      defaultMessage: 'Image',
                      id: 'Generics.Image',
                    })}
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full bg-checkerboard text-content-on-background">
                    <EntityImage
                      iconStyle={iconStyle}
                      displayColor={displayColor}
                      entity={{ image: value, name: value.id }}
                      size="4x"
                    />
                  </div>
                )}
              </>
            ) : (
              <FileDropZone.Label
                actionTitle={
                  actionTitle ||
                  $t({
                    defaultMessage:
                      'Click to select an icon, upload an image, or drag and drop it here.',
                    id: 'Blocks.Navbar.ClickToUploadImage',
                  })
                }
              />
            )}
          </div>
        </FileDropZone>
      </PopoverImagePicker>
      {!!helperText && (
        <FormControl.HelperText>{helperText}</FormControl.HelperText>
      )}
    </div>
  )
}
